import { useMemo, useState } from 'react';
import { AlertTriangle, Bell, Info, ListChecks } from 'lucide-react';
import { useDemoMode } from '../demoMode';
import { useNotifications } from '../notifications/NotificationsContext';
import { useCrm } from '../store';
import { PageTitle } from '../ui';
import RelancesClientSection from '../components/RelancesClientSection';
import RelancesFacturesSection from '../components/RelancesFacturesSection';

export default function Taches() {
  const { unreadCount } = useNotifications();
  const data = useCrm();
  const [relanceTab, setRelanceTab] = useState<'dossiers' | 'factures'>('dossiers');
  const [showDossiersHelp, setShowDossiersHelp] = useState(false);
  const [showFacturesHelp, setShowFacturesHelp] = useState(false);
  const [demo] = useDemoMode();

  // Badges des onglets
  const dossiersCount = useMemo(() => {
    const TERMINAL = new Set(['Refusée', 'Réalisée', 'Payé & Livré', 'Expirée', 'Annulée', 'Brouillon abandonné']);
    let days = 3;
    try {
      const raw = localStorage.getItem('crm.relances.inactivityDays');
      const n = raw ? parseInt(raw, 10) : 3;
      if (Number.isFinite(n) && n > 0) days = n;
    } catch { /* noop */ }
    let customMap: Record<string, Array<{ done?: boolean }>> = {};
    try { customMap = JSON.parse(localStorage.getItem('crm.relances.customReminders') || '{}'); } catch { /* noop */ }
    const daysSince = (iso?: string) => {
      if (!iso) return 0;
      const t = new Date(iso).getTime();
      return Number.isFinite(t) ? Math.floor((Date.now() - t) / 86400000) : 0;
    };
    return data.prest.filter(p => {
      if (p.archivedAt) return false;
      if (TERMINAL.has(p.statut)) return false;
      if (!demo && !/relance|propal|demande/i.test(p.statut)) return false;
      if ((customMap[p.id] || []).some(r => !r.done)) return true;
      return daysSince(p.updatedAt || p.dateCreation) >= days;
    }).length;
  }, [data.prest, demo]);

  const facturesCount = useMemo(
    () => (data.factures || []).filter(f => {
      if (['Payée', 'Annulée', 'Brouillon'].includes(f.statut)) return false;
      const ttc = f.lignes.reduce((s, l) => s + l.quantite * l.prixUnitaireHT * (1 + l.tvaPct / 100), 0) * (1 - (f.remisePct || 0) / 100);
      return Math.max(0, ttc - (f.montantPaye || 0)) > 0.01;
    }).length,
    [data.factures]
  );

  return (
    <div className="space-y-6">
      <PageTitle
        title="Relances"
        subtitle="Dossiers à relancer, factures impayées & alertes automatiques"
        icon={<ListChecks className="text-amber-400" size={24} strokeWidth={1.5} />}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <KPI label="Alertes" value={unreadCount} accent={unreadCount > 0 ? 'red' : undefined} />
        <KPI label="Dossiers à relancer" value={dossiersCount} accent="sky" />
        <KPI label="Factures impayées" value={facturesCount} accent="red" />
      </div>

      {/* Switch 2 onglets : Relances dossiers / Relances factures */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-1 inline-flex w-full sm:w-auto">
        <button
          type="button"
          onClick={() => setRelanceTab('dossiers')}
          className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-widest inline-flex items-center justify-center gap-2 transition ${
            relanceTab === 'dossiers'
              ? 'bg-sky-500 text-slate-950'
              : 'text-slate-400 hover:text-sky-300'
          }`}
        >
          <Bell size={14} strokeWidth={1.5} /> Relances dossiers
          {dossiersCount > 0 && (
            <span className={`ml-1 min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-bold ${relanceTab === 'dossiers' ? 'bg-slate-950 text-sky-400' : 'bg-sky-500/20 text-sky-300'}`}>
              {dossiersCount > 99 ? '99+' : dossiersCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setRelanceTab('factures')}
          className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-widest inline-flex items-center justify-center gap-2 transition ${
            relanceTab === 'factures'
              ? 'bg-rose-500 text-slate-950'
              : 'text-slate-400 hover:text-rose-300'
          }`}
        >
          <AlertTriangle size={14} strokeWidth={1.5} /> Relances factures
          {facturesCount > 0 && (
            <span className={`ml-1 min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-bold ${relanceTab === 'factures' ? 'bg-slate-950 text-rose-400' : 'bg-rose-500/20 text-rose-300'}`}>
              {facturesCount > 99 ? '99+' : facturesCount}
            </span>
          )}
        </button>
      </div>

      {/* ───────── Onglet Relances dossiers ───────── */}
      {relanceTab === 'dossiers' && (
        <div className="space-y-5">
          <div className="bg-slate-900/40 border border-sky-500/20 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setShowDossiersHelp(o => !o)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/30"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-sky-300">
                <Info size={14} strokeWidth={1.5} />
                Comment fonctionnent les relances&nbsp;dossiers&nbsp;?
              </div>
              <span className="text-slate-500 text-xs">{showDossiersHelp ? '−' : '+'}</span>
            </button>
            {showDossiersHelp && (
              <div className="px-4 pb-4 pt-1 text-xs text-slate-300 leading-relaxed space-y-3">
                <p>
                  Cet onglet liste <strong className="text-sky-300">tous les dossiers en attente d'action</strong> tant qu'ils ne sont pas signés/réalisés, ainsi que les rappels programmés sur chaque prestation.
                </p>
                <div>
                  <div className="text-sky-300 font-semibold mb-1">Quels dossiers apparaissent&nbsp;?</div>
                  <p className="text-slate-400">
                    Toutes les prestations dont le statut nécessite encore une action&nbsp;: <em>Demande de Rdv, Propal A FAIRE, Propal transmise, Relance, Contrat Transmis, Projet en pause</em>.
                  </p>
                  <p className="text-slate-400 mt-1">
                    Dès qu'un dossier passe à <strong>Signé</strong>, <strong>Réalisée</strong>, <strong>Refusée</strong>, <strong>Expirée</strong> ou <strong>Payé&nbsp;&&nbsp;Livré</strong>, il disparaît automatiquement&nbsp;: les relances basculent alors côté facture.
                  </p>
                </div>
                <div>
                  <div className="text-sky-300 font-semibold mb-1">Cadence des relances</div>
                  <p className="text-slate-400">
                    Les rappels sont générés automatiquement à <strong>J+3, J+7, J+15, J+30</strong> à partir de l'envoi de la proposition (statut «&nbsp;Propal transmise&nbsp;»).
                  </p>
                  <p className="text-slate-400 mt-1">
                    Tu peux à tout moment&nbsp;: <strong>noter une relance manuelle</strong> (téléphone, mail, message), <strong>reprogrammer</strong> la prochaine, ou <strong>mettre en pause</strong> via le bouton dédié.
                  </p>
                </div>
                <p className="text-slate-500 italic">
                  Astuce&nbsp;: les dossiers sont triés par urgence — les plus en retard remontent en haut.
                </p>
              </div>
            )}
          </div>

          <div className="bg-slate-900/40 border border-sky-500/30 rounded-xl p-4">
            <RelancesClientSection />
          </div>
        </div>
      )}

      {/* ───────── Onglet Relances factures ───────── */}
      {relanceTab === 'factures' && (
        <div className="space-y-5">
          <div className="bg-slate-900/40 border border-rose-500/20 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setShowFacturesHelp(o => !o)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/30"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-rose-300">
                <Info size={14} strokeWidth={1.5} />
                Comment fonctionnent les relances&nbsp;factures&nbsp;?
              </div>
              <span className="text-slate-500 text-xs">{showFacturesHelp ? '−' : '+'}</span>
            </button>
            {showFacturesHelp && (
              <div className="px-4 pb-4 pt-1 text-xs text-slate-300 leading-relaxed space-y-3">
                <p>
                  Cet onglet suit <strong className="text-rose-300">toutes les factures émises non soldées</strong>, et classe chaque impayé selon son niveau de retard pour adapter le ton de la relance.
                </p>
                <div>
                  <div className="text-rose-300 font-semibold mb-1">Les 5 niveaux de relance</div>
                  <ul className="list-disc list-inside text-slate-400 mt-1 space-y-1">
                    <li><strong className="text-emerald-300">À venir</strong> — échéance pas encore atteinte (suivi préventif).</li>
                    <li><strong className="text-amber-300">J+15</strong> — premier rappel courtois.</li>
                    <li><strong className="text-orange-300">J+30</strong> — relance ferme.</li>
                    <li><strong className="text-red-300">J+45</strong> — mise en demeure imminente.</li>
                    <li><strong className="text-red-400">J+60&nbsp;et&nbsp;plus</strong> — contentieux, action obligatoire.</li>
                  </ul>
                </div>
                <div>
                  <div className="text-rose-300 font-semibold mb-1">Suivi des paiements</div>
                  <p className="text-slate-400">
                    Pour chaque facture tu peux saisir un <strong>paiement partiel ou total</strong>. Dès qu'une facture est totalement payée, elle disparaît de la liste.
                  </p>
                  <p className="text-slate-400 mt-1">
                    Les <strong>échéanciers</strong> (acomptes, soldes) sont gérés ligne par ligne&nbsp;: chaque échéance dépassée crée sa propre alerte.
                  </p>
                </div>
                <div>
                  <div className="text-rose-300 font-semibold mb-1">Relance par email</div>
                  <p className="text-slate-400">
                    Un bouton <strong>Relancer par mail</strong> envoie directement au client un message adapté au niveau de retard, avec la facture en PJ.
                  </p>
                </div>
                <p className="text-slate-500 italic">
                  Astuce&nbsp;: la couleur de la pastille à gauche de chaque facture indique le niveau d'urgence en un coup d'œil.
                </p>
              </div>
            )}
          </div>

          <div className="bg-slate-900/40 border border-rose-500/30 rounded-xl p-4">
            <RelancesFacturesSection />
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, accent }: { label: string; value: number; accent?: 'amber' | 'sky' | 'emerald' | 'red' }) {
  const colors: Record<string, string> = {
    amber: 'text-amber-400 border-amber-500/30',
    sky: 'text-sky-400 border-sky-500/30',
    emerald: 'text-emerald-400 border-emerald-500/30',
    red: 'text-red-400 border-red-500/30',
  };
  const cls = accent ? colors[accent] : 'text-slate-200 border-slate-700';
  return (
    <div className={`bg-slate-900/60 border ${cls} rounded-xl p-3`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-2xl font-heading mt-1 ${accent ? '' : 'text-slate-100'}`}>{value}</div>
    </div>
  );
}
